import { useContext, useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import useAxios from "../../../../utils/useAxios";
import AuthContext from "../../../../context/AuthContext";
import { ExpandableSection } from "routes/skus/components/expandable-section";
import { useMoldContext } from "../../../../context/MoldContext";
import { getMolds } from "../../../../helpers/mold.helper";
import OptionFormatter from "../../../../components/OptionFormatter";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import { fetchMold } from "../../../../hooks/useMold";
import useArtworkCollection from "../../hooks/useArtworkCollectionHook";

import {
  formatCollectionOptions,
  getCollections,
} from "../../helpers/collections.helper";
import { Predicates } from "../../../../libraries/predicates/predicates";
import useMetaArtworkNameAndNumberRequests from "../../hooks/useArtworkNameAndNumberRequestsHook";
import {
  formatArtworkNameOptions,
  getArtworkName,
} from "../../helpers/artworkName.helper";
import ArtworkUploadModal from "../../components/artwork-upload-modal";
import { useMasterArtworkPrintingTechniqueStore } from "../../../../context/MasterArtworkPrintingTechniqueContext";
import { MasterArtworkPrintingTechniqueActions } from "../../../../states/meta-artwork/printing-technique";
import {
  formatArtworkNumberOptions,
  getArtworkNumber,
} from "routes/master-artwork/helpers/artworkNumber.helper";
import useArtworkNumber from "../../hooks/useArtworkNumberHook";
import { formatColorIntoOption, getColors } from "../../../../helpers/color.helper";
import { formatIntoOption } from "../../../skus/features/sku-create/sku-create.helper";
import { fetchColor } from "../../../../hooks/useColor";
import { useColorContext } from "../../../../context/ColorContext";
import { formatDecorTechIntoOption, getDecorTechs } from "../../../../helpers/decor-tech.helper";
import { useDecorationTechContext } from "../../../../context/DecorationTechContext";
import ReactModal from "react-modal";
import PictureModal from "../../../../components/PictureModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import Spinner from "../../../../components/Spinner";
import { DateTimeFormat } from "../../../../libraries/date-time-format/date-time-format";
import {
  artworkDesignGlobalPermissions,
  artworkMasterDataGlobalPermissions,
} from "../../helpers/artworkPermissions";
import isReadOnly from "../../helpers/isReadOnlyHelper";
import ArtworkPrintingTechniqueActionButtonsGroup from "../../components/printing-technique-action-buttons/printingTechniqueActionButtons";
import Banner from "../../../../components/Banner";
import ArtworkPictureModal from "routes/master-artwork/components/ArtworkPicutureModal";
import useArtworkName from "routes/master-artwork/hooks/useArtworkNameHook";
import ArtworkRequestHistoryTable from "../../components/ArtworkRequestHistoryComponent";
import useArtworkGenericRequests from "../../hooks/useArtworkGenericReaquestsHook";
import { MasterArtworkPrintingTechniqueState } from "../../../../states/meta-artwork/printing-technique/data.type";
import useArtworkPrintingTechnique from "../../hooks/useArtworkPrintingTechniqueHook";

export const PrintingTechniqueCreatePage = (props: any) => {
  const axios = useAxios();
  const authContext = useContext(AuthContext);
  const username = authContext?.username;
  const { id, code } = useParams();
  const location = useLocation();

  const hasCreatorPermission = artworkDesignGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );
  const hasApproverPermission = artworkMasterDataGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState<boolean>(
    () => !!urlSearchParams.get("editing") && hasApproverPermission,
  );

  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);

  const { state, dispatch } = useMasterArtworkPrintingTechniqueStore();

  const { data: moldsFetched, isLoading: isMoldsLoading } = useMoldContext();

  const loadMolds = async (input: string, callback: any) => {
    let options: any = getMolds(moldsFetched);
    if (input.length > 2) {
      const response = await fetchMold({ search: input, axios });
      options = getMolds(response);
    }
    callback(options);
  };

  const { data: collectionsData, isLoading: collectionsIsLoading } =
    useArtworkCollection({});
  const collectionsOptions = getCollections(collectionsData);

  const { data: artworkNameData, isLoading: artworkNameIsLoading } =
    useArtworkName({ search: state.collection?.code });
  const artworkNameOptions = getArtworkName(artworkNameData);

  const { data: artworkNumberData, isLoading: artworkNumberIsLoading } =
    useArtworkNumber({ search: state.artwork_name?.code });
  const artworkNumberOptions = getArtworkNumber(artworkNumberData);

  const { data: decorationTechs, isLoading: isDecoTechsLoading } =
    useDecorationTechContext();

  const { data: colors, isLoading: isColorsLoading } = useColorContext();

  const loadColorOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchColor({
      search,
      axios,
    });

    callback(getColors(response));
  };

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isSaveOrSubmitInProgress, setIsSaveOrSubmitInProgress] =
    useState<boolean>(false);

  const [artworkNumberImg, setArtworkNumberImg] = useState<any>();
  const [tecnicalFileImg, setTecnicalFileImg] = useState<any>();
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [showTechnicalFileModal, setShowTechnicalFileModal] = useState(false);

  const getArtworkNumberImage = async (fileName: string) => {
    const response = await axios.get(
      `/artwork-master/meta-artwork/number-upload-image/?image_file_name=${fileName}`,
    );
    setArtworkNumberImg(response.data);
  };

  const getTechnicalFileImage = async (fileName: string) => {
    const response = await axios.get(
      `/artwork-master/meta-artwork/number-upload-image/?image_file_name=${fileName}`,
    );

    setTecnicalFileImg(response.data);
    // dispatch(MasterArtworkPrintingTechniqueActions.setImageUrl(response.data));
  };
  const closeModal = () => {
    setShowPictureModal(false);
    setShowTechnicalFileModal(false);
  };

  useEffect(() => {
    // @ts-ignore
    if (!!state.artwork_number?.file_uploaded) {
      // @ts-ignore
      getArtworkNumberImage(state.artwork_number.file_uploaded);
    } else {
      setArtworkNumberImg("");
    }
  }, [state.artwork_number]);

  const getArtworkPrintingTechniqueRequest = async (id: string | number) => {
    const response = await axios.get(`/request/${id}`);
    const requestStatus = response.data.status;
    const isReadOnlyValue = isReadOnly(
      response.data.status,
      hasApproverPermission,
    );

    const pt_code =
      response.data.status === "APPROVED"
        ? `${response.data?.data?.decoration_technique?.id.toString().padStart(2, 0)}-${response.data?.corresponding_object_id.toString().padStart(5, 0)}`
        : null;

    dispatch(
      MasterArtworkPrintingTechniqueActions.setPrintingTechnique({
        ...state,
        ...response.data.data,
        code: pt_code,
        requestStatus: requestStatus,
        requestId: response.data.id,
        isReadOnly: isReadOnlyValue,
      }),
    );

    setIsLoading(false);
  };

  const [historyRecords, setHistoryRecords] = useState<any[]>([]);

  const getRequestHistory = async (id: string | number) => {
    const response = await axios.get(`/request-history/?request__id=${id}`);

    // @ts-ignore
    setHistoryRecords(response.data);
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      getArtworkPrintingTechniqueRequest(id);
      getRequestHistory(id);
      return;
    }
    dispatch(MasterArtworkPrintingTechniqueActions.resetState());
    setArtworkNumberImg("");
  }, [id, location]);

  useEffect(() => {
    if (!!state.technical_file_url) {
      // @ts-ignore
      getTechnicalFileImage(state.technical_file_url);
    }
  }, [id, state.technical_file_url, location]);

  const getMissingConditionsMessage = () => {
    const levelRequired = "full";

    const conditions = [
      {
        condition: (state: any) =>
          state.name.length > 3 &&
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.name),
        message: `<strong>Must have a name</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state.collection?.name,
          ),
        message: `<strong>Collection</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state.artwork_name?.name,
          ),
        message: `<strong>Artwork Name</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state.artwork_number?.code,
          ),
        message: `<strong>Artwork Number</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.molds),
        message: `<strong>Mold</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state.decoration_technique?.name,
          ),
        message: `<strong>Decoration technique</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.color?.name),
        message: `<strong>Color</strong> field is required`,
        conditionLevel: "full",
      },
    ];

    const missingConditions = conditions.filter(
      (condition) =>
        (levelRequired === "full"
          ? true
          : condition.conditionLevel === levelRequired) &&
        !condition.condition(state),
    );

    if (missingConditions.length < 1) {
      return "";
    }

    let message =
      "You cannot proceed to collection creation. The following requirements are missing <br> <ul class='mb-0'>";
    message += missingConditions
      .map((condition) => `<li>${condition.message}</li>`)
      .join("");
    return message + "</ul>";
  };

  useEffect(() => {
    const isValid = !!getMissingConditionsMessage();

    dispatch(MasterArtworkPrintingTechniqueActions.setIsValid(!isValid));

    if (state.requestStatus === null || state.requestStatus === "") {
      dispatch(
        MasterArtworkPrintingTechniqueActions.setRequestStatus("CREATED"),
      );
    }
  }, [
    state.artwork_name,
    state.artwork_number,
    state.collection,
    state.molds,
    state.color,
    state.decoration_technique,
  ]);

  const [searchParams, setSearchParams] = useState<any>({
    limit: 1,
    lastDays: -1,
    requestor: "",
    context: [45],
    status: ["APPROVED"],
    search: "",
    corresponding_object_id: `${code}`,
    hasFilterApplyed: false,
    offset: 0,
  });


  const getPrintingTechnique = async (code: string) => {
    setIsLoading(true);
    const response = await axios.get(
      `/artwork-master/meta-artwork/printing-technique/${code}/`,
    );


    setIsLoading(false);

    if (response) {
      const printingTechniqueDTO: MasterArtworkPrintingTechniqueState = {
        ...response.data,
      };

      dispatch(
              MasterArtworkPrintingTechniqueActions.setPrintingTechnique({
                ...state,
                ...printingTechniqueDTO,
                //@ts-ignore
                technical_file_url:printingTechniqueDTO.picture,
                // @ts-ignore
                code: `${printingTechniqueDTO.decoration_technique?.id?.toString().padStart(2, 0)}-${printingTechniqueDTO.id?.toString().padStart(5, 0)}`,
                //@ts-ignore
                requestId: printingTechniqueDTO.request_id,
                //@ts-ignore
                requestStatus: "APPROVED"
              }),
            );
            dispatch(MasterArtworkPrintingTechniqueActions.setIsReadOnly(!isEditing));
    }
    return
  };


  useEffect(() => {
    if (code) {
      getPrintingTechnique(code);
      dispatch(MasterArtworkPrintingTechniqueActions.setIsReadOnly(!isEditing));
    }
  }, [code]);

  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div
          className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
          style={{ minHeight: "70vh" }}
        >
          <h6 className="text-primary fw-bold card-header border-0 mb-2">
            Artwork Hierarchy
          </h6>
          <div className="row mb-0">
            <div className="col-md-4 mb-2 mt-1">
              <label className="form-label">Collection Code/Name*</label>
              <AsyncSelect
                name="collection"
                value={
                  Predicates.isNotNullAndNotUndefined(state.collection)
                    ? formatCollectionOptions(state.collection)
                    : null
                }
                onChange={(e) => {
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setCollection(
                      e?.value ?? null,
                    ),
                  );
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setArtworkName(null),
                  );
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setArtworkNumber(
                      null,
                    ),
                  );
                }}
                defaultOptions={collectionsOptions}
                loadOptions={(input, callback) => {
                  searchDebounce(
                    collectionsOptions,
                    input,
                    getCollections(collectionsData),
                    callback,
                  );
                }}
                formatOptionLabel={OptionFormatter}
                isLoading={collectionsIsLoading}
                isClearable
                isSearchable
                placeholder="(min 3 characters)"
                classNamePrefix="react-select"
                isDisabled={state.isReadOnly}
              />
            </div>

            <div className="col-md-4 mb-2 mt-1">
              <label className="form-label">Meta Artwork Code/Name*</label>
              <AsyncSelect
                name="artwork-name-code"
                value={
                  Predicates.isNotNullAndNotUndefined(state.artwork_name)
                    ? formatArtworkNameOptions(state.artwork_name)
                    : null
                }
                onChange={(e) => {
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setArtworkName(
                      e?.value ?? null,
                    ),
                  );
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setCollection(
                      e?.value.collection ?? null,
                    ),
                  );
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setArtworkNumber(
                      null,
                    ),
                  );
                }}
                defaultOptions={artworkNameOptions}
                loadOptions={(input, callback) => {
                  searchDebounce(
                    artworkNameOptions,
                    input,
                    getArtworkName(artworkNameData),
                    callback,
                  );
                }}
                formatOptionLabel={OptionFormatter}
                isLoading={artworkNameIsLoading}
                isClearable
                isSearchable
                placeholder="(min 3 characters)"
                classNamePrefix="react-select"
                isDisabled={state.isReadOnly}
                key={`${state.collection?.id}_artwork_name`}
              />
            </div>

            <div className="col-md-4 mb-2 mt-1">
              <label className="form-label">Meta Artwork Number*</label>
              <AsyncSelect
                name="artworkName"
                value={
                  Predicates.isNotNullAndNotUndefined(state.artwork_number)
                    ? formatArtworkNumberOptions(state.artwork_number)
                    : null
                }
                onChange={(e: any) => {
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setArtworkNumber(
                      e?.value ?? null,
                    ),
                  );
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setArtworkName(
                      e?.value.artwork_name ?? null,
                    ),
                  );
                  dispatch(
                    MasterArtworkPrintingTechniqueActions.setCollection(
                      e?.value.collection ?? null,
                    ),
                  );
                }}
                defaultOptions={artworkNumberOptions}
                loadOptions={(input, callback) => {
                  searchDebounce(
                    artworkNumberOptions,
                    input,
                    getArtworkNumber(artworkNumberData),
                    callback,
                  );
                }}
                formatOptionLabel={OptionFormatter}
                isLoading={artworkNumberIsLoading}
                isClearable
                isSearchable
                placeholder="(min 3 characters)"
                classNamePrefix="react-select"
                isDisabled={state.isReadOnly}
              />
            </div>
          </div>
          <div className="row mb-0">
            <div className="col-md-auto mb-0 d-md-flex flex-wrap flex-fill">
              <div className="col-lg-3 mb-0 form-group">
                <label className="form-label">Collection Launch Date</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state?.collection?.launch_date}
                  placeholder=""
                  disabled
                  key={state?.collection?.launch_date}
                />
              </div>
              <div className="col-lg-3 mb-0 form-group">
                <label className="form-label">Collection Licensor</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state.collection?.licensor?.name}
                  key={state.collection?.licensor?.name}
                  disabled
                />
              </div>
              <div className="col-lg-3 mb-0 form-group">
                <label className="form-label">Collection License</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state.collection?.licensed_property?.name}
                  key={state.collection?.licensed_property?.name}
                  disabled
                />
              </div>
              <div className="col-lg-3 mb-0 form-group">
                <label className="form-label">Meta Artwork Launch Date</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state.artwork_name?.launch_date?.split(" ")[0]}
                  key={state.artwork_name?.launch_date?.split(" ")[0]}
                  disabled
                />
              </div>
            </div>
            <div className="col-md-auto mb-0 form-group">
              <label className="form-label">Meta Artwork Image</label>
              {!!artworkNumberImg ? (
                <div className="img-container d-flex">
                  <img
                    className="d-flex"
                    onClick={() => setShowPictureModal(true)}
                    alt="Image of the product"
                    src={`data:image/jpeg;base64,${artworkNumberImg}`}
                    key={state.technical_file_url}
                  />
                </div>
              ) : (
                <div className="img-placeholder d-flex justify-content-center align-items-center">
                  <span className="fw-bold text-center text-gray-800">
                    No Picture Available
                  </span>
                </div>
              )}
            </div>
          </div>
          <ExpandableSection
            className="mb-2 mt-4"
            sectionId="Create new Artwork"
            title="Create new Artwork"
            expandedByDefault={true}
          >
            <div className="row mb-0">
              {/*{!!id || !!code && (*/}
              {state?.code && (
                <div className="col-lg-2 form-group">
                  <label className="form-label">Artwork Code*</label>
                  <input
                    // onChange={(e) =>
                    //   dispatch(
                    //     MasterArtworkPrintingTechniqueActions.setCode(
                    //       e.target.value,
                    //     ),
                    //   )
                    // }
                    value={state?.code}
                    placeholder=""
                    type="text"
                    className="form-control d-inline"
                    disabled={true}
                  />
                </div>
              )}

              <div
                className={
                  !!id || !!code ? "col-lg-4 form-group" : "col-lg-6 form-group"
                }
              >
                <label className="form-label">Artwork Name*</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkPrintingTechniqueActions.setName(
                        e.target.value,
                      ),
                    )
                  }
                  value={state.name}
                  placeholder=""
                  // minLength={8}
                  // maxLength={8}
                  type="text"
                  className="form-control d-inline"
                  disabled={state.isReadOnly}
                />
              </div>

              <div className="col-lg-4 form-group">
                <label className="form-label">Mold(s)*</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadMolds,
                      input,
                      getMolds(moldsFetched) ?? [],
                      callback,
                    );
                  }}
                  defaultOptions={getMolds(moldsFetched)}
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkPrintingTechniqueActions.setMolds(
                        e.map((moldOption) => moldOption.value),
                      ),
                    )
                  }
                  value={getMolds(state.molds ?? [])}
                  formatOptionLabel={OptionFormatter}
                  // isOptionDisabled={() =>
                  //   Predicates.isNotNullAndNotUndefined(state.molds) &&
                  //   state.molds.length >= 5
                  // }
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="(min 3 characters, up to 5 molded components)"
                  isClearable
                  isLoading={isMoldsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state.isReadOnly}
                />
              </div>

              <div className="col-lg-2 form-group">
                <label htmlFor="purchased_part" className="form-label">
                  Or purchased part / Sub-Assembly*
                </label>
                <Select
                  classNamePrefix="react-select"
                  options={[
                    { label: "Yes", value: "Y" },
                    { label: "No", value: "N" },
                  ]}
                  defaultValue={{ label: "No", value: "N" }}
                  onChange={(e) => {
                    dispatch(
                      MasterArtworkPrintingTechniqueActions.stPurchasedPartOrSubAssembly(
                        e?.value ?? null,
                      ),
                    );
                  }}
                  isDisabled={state.isReadOnly}
                />
              </div>
            </div>
            <div className="row mb-0 mt-1">
              <div className="col-lg-6">
                <label className="form-label">Decoration Technique*</label>
                <Select
                  options={getDecorTechs(
                    decorationTechs.slice(1, decorationTechs.length),
                  )}
                  onChange={(e: any) =>
                    dispatch(
                      MasterArtworkPrintingTechniqueActions.setDecorationTechnique(
                        e.value,
                      ),
                    )
                  }
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      state.decoration_technique,
                    )
                      ? formatDecorTechIntoOption(state.decoration_technique)
                      : null
                  }
                  placeholder=""
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  isLoading={isDecoTechsLoading}
                  isClearable
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state.isReadOnly}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">Artwork Color*</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadColorOptions,
                      input,
                      getColors(colors),
                      callback,
                    );
                  }}
                  defaultOptions={getColors(colors)}
                  onChange={(e: any) =>
                    dispatch(
                      MasterArtworkPrintingTechniqueActions.setColor(e.value),
                    )
                  }
                  //@ts-ignore
                  // value={formatIntoOption(state?.color?.id, state?.color?.name)}
                  value={
                    Predicates.isNotNullAndNotUndefined(state.color)
                      ? formatColorIntoOption(state.color)
                      : null
                  }
                  placeholder="(min 3 characters)"
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={isColorsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state.isReadOnly}
                />
              </div>

              <div className="row p-0 mt-1">
                <div className="col-md-auto form-group mb-0">
                  <label className="form-label">Technical file</label>
                  {state.isReadOnly ? (
                    !!state.technical_file_url ? (
                      <div className="img-container d-flex">
                        <img
                          className="d-flex"
                          onClick={() => setShowTechnicalFileModal(true)}
                          alt="Image of the product"
                          src={`data:image/jpeg;base64,${tecnicalFileImg}`}
                          key={state.technical_file_url}
                        />
                      </div>
                    ) : (
                      <div className="img-placeholder d-flex justify-content-center align-items-center">
                        <span className="fw-bold text-center text-gray-800">
                          No Picture Available
                        </span>
                      </div>
                    )
                  ) : (
                    <button
                      onClick={() => {
                        {
                          !state.technical_file_url && setShowUploadModal(true);
                        }
                        {
                          state.technical_file_url &&
                            setShowTechnicalFileModal(true);
                        }
                      }}
                      type="button"
                      className="icon-button upload-button form-control flex-fill d-block w-100 mx-auto"
                    >
                      {state.technical_file_url ? (
                        <img
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/img/uploadSuccess.png"
                          }
                          alt="upload success"
                          className="mb-3"
                          width={30}
                        ></img>
                      ) : (
                        <img
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/img/uploadPending.png"
                          }
                          alt="upload pending"
                          className="mb-3 col-md-auto"
                          width={26}
                        ></img>
                      )}
                    </button>
                  )}
                </div>

                <div className="col-md-auto form-group flex-fill">
                  <label className="form-label">
                    Comments and Technical Requests
                  </label>
                  <textarea
                    onChange={(e) =>
                      dispatch(
                        MasterArtworkPrintingTechniqueActions.setCommentsAndTechnicalFileRequests(
                          e.target.value,
                        ),
                      )
                    }
                    value={state.comments_and_technical_file_requests}
                    // placeholder="Free-text field, expandable, max 500 characters?"
                    maxLength={500}
                    rows={1}
                    className="form-control d-inline"
                    disabled={state.isReadOnly}
                    style={{
                      height:
                        state.isReadOnly && !!tecnicalFileImg ? "80%" : "",
                      maxHeight: "128px",
                    }}
                  />
                </div>
              </div>
            </div>
          </ExpandableSection>

          {historyRecords.length > 0 && (
            <ArtworkRequestHistoryTable historyRecords={historyRecords} />
          )}

          {!state.isValid && !state.isReadOnly && (
            <>
              <Banner
                classname={"mt-3"}
                message={getMissingConditionsMessage()}
                type="danger"
              />
            </>
          )}

          <ReactModal
            isOpen={showUploadModal}
            className="custom-modal barcode-img-modal"
            overlayClassName="custom-overlay"
          >
            <ArtworkUploadModal
              setArtworkImage={(e: any) =>
                dispatch(
                  MasterArtworkPrintingTechniqueActions.setTechnicalFileUrl(e),
                )
              }
              uploadedImage={tecnicalFileImg}
              handleCloseModal={() => setShowUploadModal(false)}
            />
          </ReactModal>

          <ArtworkPrintingTechniqueActionButtonsGroup
            //@ts-ignore
            isReadOnlyRequest={state.isReadOnly}
            isEditing={Predicates.isNotNullAndNotUndefinedAndNotEmpty(
              isEditing,
            )}
            isSaveOrSubmitInProgress={isSaveOrSubmitInProgress}
            username={username ?? ""}
            hasApproverPermission={hasApproverPermission}
            hasCreatorPermission={hasCreatorPermission}
            setIsSaveOrSubmitInProgress={setIsSaveOrSubmitInProgress}
          />
        </div>
      )}
      <ReactModal
        isOpen={showPictureModal}
        className="custom-modal mold-comps"
        overlayClassName="custom-overlay"
      >
        <ArtworkPictureModal
          handleCloseModal={closeModal}
          product={{
            imageName: `Artwork Number ${state.artwork_number?.code}`,
            image_file: `data:image/jpeg;base64,${artworkNumberImg}`,
          }}
        />
      </ReactModal>
      <ReactModal
        isOpen={showTechnicalFileModal}
        className="custom-modal mold-comps"
        overlayClassName="custom-overlay"
      >
        <ArtworkPictureModal
          handleCloseModal={closeModal}
          product={{
            imageName: `Technical file ${state.technical_file_url}`,
            image_file: `data:image/jpeg;base64,${tecnicalFileImg}`,
          }}
        />

        {!state.isReadOnly && (
          <div className="row justify-content-center mb-3">
            <button
              className="btn btn-danger col-2 mx-auto mt-1 mb-1 justify-self-center"
              onClick={() => {
                dispatch(MasterArtworkPrintingTechniqueActions.setImageUrl(""));
                dispatch(
                  MasterArtworkPrintingTechniqueActions.setTechnicalFileUrl(""),
                );

                closeModal();
              }}
            >
              Remove image
            </button>
          </div>
        )}
      </ReactModal>
    </>
  );
};
